import React, { useState } from "react";
import { toast } from "sonner";
import { Send, MapPin, Phone, Mail } from "lucide-react";
import api, { formatApiErrorDetail, WHATSAPP } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", phone: "", category: "General" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [refNumber, setRefNumber] = useState("");

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return toast.error("Please fill in all required fields");
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setRefNumber(data.ref_number || "");
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="contact-page">
      <div className="text-center mb-14">
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Get in Touch</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-display text-primary">Contact Us</h1>
        <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
          Have a question about our products, need help with an order, or just want to say hello?
          We'd love to hear from you.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
        {/* Contact Info */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="text-lg font-display text-primary mb-4">Reach Us Directly</h2>
            <div className="space-y-5">
              <a
                href={`https://wa.me/${WHATSAPP}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-whatsapp"
              >
                <div className="w-10 h-10 bg-primary/10 flex items-center justify-center shrink-0">
                  <Phone size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">WhatsApp</p>
                  <p className="text-sm text-muted-foreground">+91 95992 72633</p>
                </div>
              </a>
              <a
                href="mailto:info@aetheldecor.com"
                className="flex items-start gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-10 h-10 bg-primary/10 flex items-center justify-center shrink-0">
                  <Mail size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">Email</p>
                  <p className="text-sm text-muted-foreground">info@aetheldecor.com</p>
                </div>
              </a>
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-10 h-10 bg-primary/10 flex items-center justify-center shrink-0">
                  <MapPin size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Office</p>
                  <p className="text-sm text-muted-foreground">Hyderabad, Telangana, India</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Business Hours</p>
            <p className="text-sm text-foreground">Mon – Sat: 10 AM – 7 PM IST</p>
            <p className="text-sm text-muted-foreground">Usually respond within 24 hours</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-3">
          {sent ? (
            <div className="border border-primary/20 bg-primary/5 p-10 text-center" data-testid="contact-success">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <Send size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-display text-primary mb-2">Message Sent!</h3>
              {refNumber && (
                <p className="text-sm text-primary font-mono mb-2">Reference: #{refNumber}</p>
              )}
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Thank you for reaching out. We've sent a confirmation to your email. Our team will review your message and get back to you within 24 hours.
              </p>
              <button
                onClick={() => { setSent(false); setRefNumber(""); setForm({ name: "", email: "", subject: "", message: "", phone: "", category: "General" }); }}
                className="mt-6 text-sm text-primary font-semibold link-underline"
                data-testid="contact-send-another"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
                    Name <span className="text-destructive">*</span>
                  </span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    required
                    className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    data-testid="contact-name-input"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
                    Email <span className="text-destructive">*</span>
                  </span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    required
                    className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    data-testid="contact-email-input"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Subject</span>
                <input
                  type="text"
                  value={form.subject}
                  onChange={set("subject")}
                  placeholder="What's this about?"
                  className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                  data-testid="contact-subject-input"
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Phone</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 XXXXX XXXXX"
                    className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    data-testid="contact-phone-input"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Category</span>
                  <select
                    value={form.category}
                    onChange={set("category")}
                    className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
                    data-testid="contact-category-input"
                  >
                    <option value="General">General Enquiry</option>
                    <option value="Sales">Sales / Pricing</option>
                    <option value="Order">Order Related</option>
                    <option value="Returns">Returns / Refunds</option>
                    <option value="Bulk Order">Bulk / Corporate Order</option>
                    <option value="Complaint">Complaint</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground">
                  Message <span className="text-destructive">*</span>
                </span>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us how we can help..."
                  required
                  rows={6}
                  className="mt-1.5 w-full bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary resize-none"
                  data-testid="contact-message-input"
                />
              </label>
              <button
                type="submit"
                disabled={sending}
                className="w-full sm:w-auto bg-primary text-primary-foreground px-10 py-3.5 text-sm tracking-wide flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground/40 border-t-primary-foreground rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send size={16} /> Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
